import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { recordingsAPI } from '@/lib/api';
import { Play, Music, Star } from 'lucide-react';

const SharedRecordingPage = () => {
  const { token } = useParams();

  const { data: recording, isLoading, error } = useQuery({
    queryKey: ['shared-recording', token],
    queryFn: async () => (await recordingsAPI.getShared(token)).data,
    enabled: !!token,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-900 to-gray-900">
        <div className="text-white text-xl font-bold">Loading recording...</div>
      </div>
    );
  }

  if (error || !recording) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-900 to-gray-900">
        <div className="text-center text-white">
          <h1 className="text-3xl font-black mb-2">Link Expired or Not Found</h1>
          <p className="text-gray-300">This share link may have expired or been removed.</p>
        </div>
      </div>
    );
  }

  const isVideo = recording.recording_type === 'video' || recording.content_type?.startsWith('video/');

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-gray-900 to-black flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white/10 backdrop-blur-md rounded-2xl overflow-hidden border border-white/20 shadow-2xl">
        {/* Header */}
        <div className="p-6 text-center border-b border-white/10">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Music size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-black text-white mb-1">{recording.title}</h1>
          <p className="text-gray-300 text-sm">
            Read by {recording.student_name}{recording.student_age ? ` (age ${recording.student_age})` : ''}
            {recording.chapter_number ? ` · Chapter ${recording.chapter_number}` : ''}
          </p>
          {recording.diction_scores && (
            <div className="flex items-center justify-center gap-1 mt-3">
              {[1,2,3,4,5].map(s => (
                <Star key={s} size={16} className={s <= Math.round(recording.diction_scores.overall / 20) ? 'text-amber-400 fill-amber-400' : 'text-gray-500'} />
              ))}
              <span className="text-sm font-bold text-amber-400 ml-2">{recording.diction_scores.overall}%</span>
            </div>
          )}
        </div>

        {/* Player */}
        <div className="p-6">
          {isVideo ? (
            <video src={recording.stream_url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${recording.stream_url}` : recording.stream_url} controls className="w-full rounded-xl" />
          ) : (
            <audio src={recording.stream_url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${recording.stream_url}` : recording.stream_url} controls className="w-full" />
          )}
          {recording.created_date && (
            <p className="text-xs text-gray-400 text-center mt-3">
              Recorded {new Date(recording.created_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 text-center">
          <p className="text-xs text-gray-400">Powered by <span className="font-bold text-amber-400">Semantic Vision</span></p>
        </div>
      </div>
    </div>
  );
};

export default SharedRecordingPage;
